import React, { useState } from 'react';

const trackEvent = (eventName, params = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, { app: 'find_my_business', ...params });
  }
};

function Results({ recommendations, email }) {
  const [selectedIdea, setSelectedIdea] = useState(null);

  const downloadResults = () => {
    if (!recommendations) return;
    trackEvent('results_downloaded', { result_type: 'business_ideas' });
    let text = 'YOUR BUSINESS IDEAS FROM FIND MY BUSINESS\n';
    text += 'by Designer of Content | designerofcontent.com\n';
    text += '='.repeat(50) + '\n\n';
    recommendations.forEach((rec, i) => {
      text += `#${i + 1}: ${rec.businessName}\n`;
      text += '-'.repeat(40) + '\n';
      text += `Difficulty: ${rec.difficulty}\n`;
      text += `Income Potential: ${rec.incomePotential}\n`;
      text += `Time to First $: ${rec.timeToFirstDollar}\n`;
      text += `Startup Cost: ${rec.startupCost}\n\n`;
      text += `About: ${rec.description}\n\n`;
      text += `Why This Works For You: ${rec.whyThisMatches}\n\n`;
      if (rec.firstSteps) text += `Your First Steps:\n${rec.firstSteps}\n\n`;
      if (rec.monetizationStrategy) text += `How You'll Make Money: ${rec.monetizationStrategy}\n\n`;
      if (rec.commonMistakes) text += `Avoid This Mistake: ${rec.commonMistakes}\n\n`;
      if (rec.platformToStart) text += `Where to Start: ${rec.platformToStart}\n\n`;
      text += '='.repeat(50) + '\n\n';
    });
    text += 'READY FOR YOUR NEXT STEP?\n\n';
    text += 'Get your complete ICP-driven marketing plan -- customer segments,\n';
    text += 'content pillars, 90-day action plan, and email templates for $47.\n\n';
    text += 'https://smallbusinesstools.designerofcontent.com/ICP/\n';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-business-ideas.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!recommendations) {
    return (
      <div className="results-container">
        <div className="results-header">
          <h2>🐕 Pokkadot is sniffing out your perfect businesses...</h2>
          <div style={{ marginTop: '2rem', fontSize: '1.1rem', lineHeight: '2' }}>
            <p>🧠 Analyzing your strengths and experience...</p>
            <p>💡 Matching you with realistic business models...</p>
            <p>🎯 Creating your personalized recommendations...</p>
            <p style={{ marginTop: '1.5rem', color: '#00d2ff', fontWeight: '600' }}>
              This takes about 20-30 seconds. Worth the wait! ✨
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="results-container">
      <div className="results-header">
        <h2>🎉 Your Business Ideas!</h2>
        <p style={{ fontSize: '1.1rem', marginTop: '1rem' }}>
          Based on your skills and what people already ask you for help with:
        </p>
      </div>

      {recommendations.map((rec, index) => (
        <div key={index} className="recommendation-card">
          <h3>#{index + 1}: {rec.businessName}</h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
            gap: '1rem', 
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#111111',
            borderRadius: '8px'
          }}>
            <div>
              <strong style={{ color: '#00d2ff' }}>Difficulty:</strong>
              <p style={{ margin: '0.25rem 0 0 0' }}>{rec.difficulty}</p>
            </div>
            <div>
              <strong style={{ color: '#00d2ff' }}>Income Potential:</strong>
              <p style={{ margin: '0.25rem 0 0 0' }}>{rec.incomePotential}</p>
            </div>
            <div>
              <strong style={{ color: '#00d2ff' }}>First $ Timeline:</strong>
              <p style={{ margin: '0.25rem 0 0 0' }}>{rec.timeToFirstDollar}</p>
            </div>
            <div>
              <strong style={{ color: '#00d2ff' }}>Startup Cost:</strong>
              <p style={{ margin: '0.25rem 0 0 0' }}>{rec.startupCost}</p>
            </div>
          </div>

          <p style={{ fontSize: '1.05rem', marginBottom: '1.5rem', lineHeight: '1.7' }}>
            {rec.description}
          </p>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ color: '#00d2ff', fontWeight: '600', marginBottom: '0.75rem' }}>
              💡 Why This Works For You:
            </p>
            <p style={{ lineHeight: '1.7' }}>{rec.whyThisMatches}</p>
          </div>
          
          {rec.firstSteps && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#00d2ff', fontWeight: '600', marginBottom: '0.75rem' }}>
                🚀 Your First Steps:
              </p>
              <ol style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                {(() => {
                  if (Array.isArray(rec.firstSteps)) {
                    return rec.firstSteps.map((step, i) => <li key={i}>{step}</li>);
                  }
                  const steps = rec.firstSteps.split(/\n+/).filter(step => step.trim());
                  return steps.map((step, i) => (
                    <li key={i}>{step.replace(/^\d+\.\s*/, '').trim()}</li>
                  ));
                })()}
              </ol>
            </div>
          )}
          
          {rec.monetizationStrategy && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#00d2ff', fontWeight: '600', marginBottom: '0.75rem' }}>
                💵 How You'll Make Money:
              </p>
              <p style={{ lineHeight: '1.7' }}>{rec.monetizationStrategy}</p>
            </div>
          )}

          {rec.platformToStart && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#00d2ff', fontWeight: '600', marginBottom: '0.75rem' }}>
                📍 Where To Start:
              </p>
              <p style={{ lineHeight: '1.7' }}>{rec.platformToStart}</p>
            </div>
          )}
          
          {rec.commonMistakes && (
            <div style={{ 
              marginTop: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#111111', 
              borderRadius: '6px',
              borderLeft: '4px solid #f20555'
            }}>
              <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                ⚠️ Avoid This Mistake:
              </p>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>{rec.commonMistakes}</p>
            </div>
          )}
        </div>
      ))}

      {/* Download Button */}
      <div style={{ textAlign: 'center', margin: '2rem 0 1rem' }}>
        <button
          onClick={downloadResults}
          style={{
            background: 'rgba(0, 210, 255, 0.15)',
            border: '2px solid #00d2ff',
            color: '#00d2ff',
            padding: '0.85rem 2rem',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Save My Results (Download)
        </button>
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
          Download a copy so you don't lose your ideas
        </p>
      </div>

      {/* Interactive "Which One?" Section */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(0, 210, 255, 0.15) 0%, rgba(242, 5, 85, 0.15) 100%)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '2.5rem',
        marginTop: '2rem',
        border: '2px solid rgba(0, 210, 255, 0.3)',
        textAlign: 'center'
      }}>
        <h3 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>
          Which One Feels Right? 🎯
        </h3>
        <p style={{ fontSize: '1.05rem', marginBottom: '2rem', opacity: 0.9 }}>
          Pick your favorite -- it personalizes your next step
        </p>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          maxWidth: '600px',
          margin: '0 auto 2rem'
        }}>
          {recommendations.map((rec, index) => (
            <button
              key={index}
              onClick={() => setSelectedIdea(index)}
              style={{
                padding: '1.25rem',
                fontSize: '1rem',
                fontWeight: '600',
                background: selectedIdea === index
                  ? 'linear-gradient(135deg, rgba(0, 210, 255, 0.3) 0%, rgba(0, 210, 255, 0.1) 100%)'
                  : '#111111',
                color: 'white',
                border: selectedIdea === index
                  ? '3px solid #00d2ff'
                  : '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left'
              }}
            >
              #{index + 1}: {rec.businessName}
            </button>
          ))}
        </div>
      </div>

      {/* Next Step Card -- always visible, personalizes when idea is selected */}
      <div style={{
        marginTop: '2rem',
        borderRadius: '16px',
        border: '2px solid #f20555',
        overflow: 'hidden',
      }}>
        {/* Label bar */}
        <div style={{
          background: '#f20555',
          padding: '0.5rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '800', letterSpacing: '0.1em', color: 'white', textTransform: 'uppercase' }}>Your Next Step</span>
        </div>

        {/* Card body */}
        <div style={{
          background: '#111111',
          padding: '2rem 2rem 2.5rem',
          textAlign: 'center'
        }}>
          {/* Price badge */}
          <div style={{
            display: 'inline-block',
            background: 'rgba(242, 5, 85, 0.12)',
            border: '1px solid #f20555',
            color: '#f20555',
            borderRadius: '30px',
            padding: '0.35rem 1.1rem',
            fontSize: '0.85rem',
            fontWeight: '700',
            marginBottom: '1.25rem',
            letterSpacing: '0.03em'
          }}>
            $47 one-time
          </div>

          <h3 style={{ fontSize: '1.6rem', fontWeight: '800', color: 'white', marginBottom: '0.75rem', lineHeight: '1.3' }}>
            You know your business.<br />Now meet your customer.
          </h3>

          <p style={{ fontSize: '1rem', color: '#cccccc', lineHeight: '1.7', marginBottom: '1.75rem', maxWidth: '480px', margin: '0 auto 1.75rem' }}>
            {selectedIdea !== null
              ? <>The ICP Assessment builds 3 detailed customer personas around <strong style={{ color: 'white' }}>{recommendations[selectedIdea].businessName}</strong> -- where to find them, how to talk to them, and exactly what makes them buy.</>
              : <>The ICP Assessment builds 3 detailed customer personas for your chosen business -- where to find them, how to talk to them, and exactly what makes them buy.</>
            }
          </p>

          <a
            href={`https://smallbusinesstools.designerofcontent.com/ICP/?email=${encodeURIComponent(email || '')}`}
            onClick={() => trackEvent('next_step_clicked', { destination: 'icp_landing' })}
            style={{
              display: 'inline-block',
              background: '#f20555',
              color: 'white',
              padding: '1rem 2.5rem',
              borderRadius: '10px',
              fontSize: '1.1rem',
              fontWeight: '700',
              textDecoration: 'none',
              transition: 'opacity 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.opacity = '0.88'}
            onMouseOut={e => e.currentTarget.style.opacity = '1'}
          >
            Take the ICP Assessment →
          </a>

          <p style={{ marginTop: '0.85rem', fontSize: '0.8rem', color: '#666666' }}>
            One-time payment. Instant access. No subscription.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Results;